/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
    Button,
    Heading,
    SimpleGrid,
    Skeleton,
    Stack,
    Text,
    VStack,
    Box,
    useDisclosure,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {Progress} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {Swiper, SwiperSlide} from 'swiper/react'
import {A11y, Keyboard, Navigation} from 'swiper/modules'
import {
    additionalInfoFullTileStyle,
    benefitsFullTileStyle,
    containerStyle,
    couponModalStyle,
    headingIconStyle,
    myRewardsTextStyle,
    pageHeadingStyle,
    pointsTileItemContainerStyle,
    pointsTilePointsStyle,
    pointsTileTextStyle,
    progressbarStyle,
    rewardFullTileStyle,
    shopNowButtonStyle,
    swiperContainerStyle,
    tileGridStyle,
    tileHeadingStyle,
    tileStyle
} from './index.style'
import {CavendersIcon, CloseIcon} from '../../../../components/icons'
import useCustomFetch from '../../../../hooks/useFetchContent'
import {fetchWrapper} from '../../../../utills/fetchWrapper'
import {CouponCard} from '../../../../components/coupon-card'

const LOYALTY_CONTENT_ASSETS_IDS = {
    loyalty_is_rewarded_here: 'loyalty_is_rewarded_here',
    loyalty_additional_benefits_just_for_members: 'loyalty_additional_benefits_just_for_members',
    loyalty_reward_terms_conditions: 'loyalty_reward_terms_conditions'
}

const REWARD_POINTS_LIMIT = 200

const AccountRewards = () => {
    const headingRef = useRef()
    const navigate = useNavigation()
    const {isOpen: openCouponModal, onOpen, onClose} = useDisclosure()
    const [selectedCoupon, setSelectedCoupon] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [rewards, setRewards] = useState({points: 0, coupons: []})
    const [content, setContent] = useState({})
    const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer()
    const {c_loyaltyCustomerNo: loyaltyCustomerNo = null} = customer
    const {data: basket} = useCurrentBasket()
    const {token} = useAccessToken()
    const {
        response: htmlStr,
        error,
        loading
    } = useCustomFetch(token, Object.values(LOYALTY_CONTENT_ASSETS_IDS).join(','), false)

    const getContentAsset = useCallback(
        (doc, key) => {
            return doc.querySelector(`#${key}`).outerHTML
        },
        [htmlStr]
    )

    const handleOnShopNowClick = useCallback(() => {
        navigate('/')
    })

    const setContentAsset = useCallback(() => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlStr, 'text/html')
        const assetIdsArr = Object.values(LOYALTY_CONTENT_ASSETS_IDS)
        const content = {}
        assetIdsArr.forEach((id) => {
            content[id] = getContentAsset(doc, id)
        })
        setContent(content)
    })

    const fetchAvailablePoints = useCallback(async () => {
        const urlEncodedParams = new URLSearchParams({
            customerId: loyaltyCustomerNo
        })
        const apiUrl = `/api/loyalty/points?${urlEncodedParams}`
        await fetchWrapper(apiUrl)
            .then(async (response) => {
                if (response.status === 400) {
                    // const message = formatMessage(MISMATCH_ERROR)
                    // form.setError('global', {type: 'manual', message})
                }
                return await response.json()
            })
            .then(({data: {value}}) => {
                if (!value?.length) {
                    setRewards({
                        points: 0
                    })
                } else {
                    setRewards({
                        points: +value[0]?.mserp_itsactivepoints || 0,
                        coupons: []
                    })
                }
            })
            .catch((err) => {
                console.log(err)
                setRewards({
                    points: 0
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [loyaltyCustomerNo])

    const fetchCoupons = useCallback(async () => {
        const urlEncodedParams = new URLSearchParams({
            customerId: loyaltyCustomerNo
        })
        const apiUrl = `/api/loyalty/coupons?${urlEncodedParams}`
        await fetchWrapper(apiUrl)
            .then(async (response) => {
                if (response.status === 400) {
                    // const message = formatMessage(MISMATCH_ERROR)
                    // form.setError('global', {type: 'manual', message})
                }
                return await response.json()
            })
            .then(({data: {value}}) => {
                if (!value?.length) {
                    fetchAvailablePoints()
                } else {
                    setRewards({
                        points: +value[0]?.mserp_itsactivepoints || 0,
                        coupons: value
                    })
                }
                setIsLoading(false)
            })
            .catch((err) => {
                console.log(err)
                setRewards({
                    points: 0,
                    coupons: []
                })
                setIsLoading(false)
            })
    }, [loyaltyCustomerNo])

    useEffect(() => {
        // Focus the 'Account Rewards' header when the component mounts for accessibility
        headingRef?.current?.focus()

        // Static swiper js css
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = getAssetUrl('static/swiper.min.css')
        document.head.appendChild(link)

        // Static swiper navigation module css
        const navLink = document.createElement('link')
        navLink.rel = 'stylesheet'
        navLink.href = getAssetUrl('static/navigation.min.css')
        document.head.appendChild(navLink)

        return () => {
            document.head.removeChild(link)
            document.head.removeChild(navLink)
        }
    }, [])

    useEffect(() => {
        if (loyaltyCustomerNo) {
            fetchCoupons()
        } else if (!isCustomerLoading && !loyaltyCustomerNo) {
            setIsLoading(false)
        }
    }, [loyaltyCustomerNo, isCustomerLoading])

    useEffect(() => {
        if (htmlStr) {
            setContentAsset()
        }
    }, [htmlStr])

    const progressbarValue = useMemo(() => {
        return Math.floor((+(rewards?.points || 0) * 100) / 200)
    }, [isLoading, rewards?.points])

    const dummyCoupons = Array.from({length: 5}).fill({})

    if (error) return null

    return (
        <>
            <Stack data-testid="account-rewards-page" {...containerStyle}>
                <Heading as="h1" tabIndex="0" ref={headingRef} {...pageHeadingStyle}>
                    <CavendersIcon {...headingIconStyle} />
                </Heading>
                {/* Info Tiles */}
                <SimpleGrid {...tileGridStyle}>
                    <Skeleton
                        isLoaded={!loading}
                        sx={rewardFullTileStyle}
                        dangerouslySetInnerHTML={{
                            __html: content?.['loyalty_is_rewarded_here'] || ''
                        }}
                    ></Skeleton>
                    <Skeleton {...tileStyle} isLoaded={!isLoading}>
                        <VStack {...pointsTileItemContainerStyle}>
                            <Heading as="h2" {...tileHeadingStyle}>
                                <FormattedMessage
                                    defaultMessage="Available Points"
                                    id="my_cavenders.my_rewards.tile_heading.available_points"
                                />
                            </Heading>
                            {rewards?.points < REWARD_POINTS_LIMIT && (
                                <Text {...pointsTileTextStyle}>
                                    {rewards?.points > 0 ? (
                                        <FormattedMessage
                                            defaultMessage="You’re only {remainingPoints} points away from your next reward!"
                                            id="my_cavenders.my_rewards.tile_description.points_away"
                                            values={{
                                                remainingPoints: (
                                                    REWARD_POINTS_LIMIT - rewards?.points
                                                ).toFixed(2)
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="You don’t have any rewards yet — start shopping and earn 1 point for every $1 you spend. Collect 200 points to unlock a $5 reward!"
                                            id="my_cavenders.my_rewards.tile_description.zero_points"
                                        />
                                    )}
                                </Text>
                            )}
                            <Text {...pointsTilePointsStyle(!rewards?.points)}>
                                {rewards?.points}
                            </Text>
                            {rewards?.points < REWARD_POINTS_LIMIT && (
                                <>
                                    <Progress {...progressbarStyle} value={progressbarValue} />
                                    <Text {...pointsTileTextStyle}>
                                        <FormattedMessage
                                            defaultMessage="of {limit} points"
                                            id="my_cavenders.my_rewards.tile_footer.of_points"
                                            values={{
                                                limit: REWARD_POINTS_LIMIT
                                            }}
                                        />
                                    </Text>
                                </>
                            )}
                        </VStack>
                    </Skeleton>
                </SimpleGrid>
                {/* Info Tiles end */}
                <Stack {...tileStyle}>
                    <Skeleton as="h2" {...tileHeadingStyle} isLoaded={!isLoading}>
                        <FormattedMessage
                            defaultMessage="My Rewards"
                            id="my_cavenders.my_rewards.tile_heading.my_rewards"
                        />
                    </Skeleton>
                    <Skeleton {...myRewardsTextStyle} isLoaded={!isLoading}>
                        {rewards?.coupons?.length ? (
                            <FormattedMessage
                                defaultMessage="Copy the promo code from your reward and apply it in the promo code section at checkout — or, if you're shopping in store, show the code to an associate at the register."
                                id="my_cavenders.my_rewards.tile_desc.copy_promo"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="You don’t have any rewards yet — start shopping and earn 1 point for every $1 you spend. Collect 200 points to unlock a $5 reward!"
                                id="my_cavenders.my_rewards.tile_desc.no_rewards"
                            />
                        )}
                    </Skeleton>
                    {isLoading || rewards?.coupons?.length ? (
                        <Box sx={swiperContainerStyle(rewards?.coupons?.length === 1)}>
                            <Swiper
                                modules={[Navigation, Keyboard, A11y]}
                                navigation
                                keyboard={{enabled: true}}
                                breakpoints={{
                                    // when window width is >= 320px
                                    320: {
                                        slidesPerView: 1.346,
                                        spaceBetween: 10
                                    },
                                    // when window width is >= 640px
                                    640: {
                                        slidesPerView: 3.25,
                                        spaceBetween: 25
                                    },
                                    // when window width is >= 797px
                                    797: {
                                        slidesPerView: 3.6,
                                        spaceBetween: 25
                                    },
                                    // when window width is >= 992px
                                    992: {
                                        slidesPerView: 2.5,
                                        spaceBetween: 15
                                    },
                                    // when window width is >= 1200px
                                    1200: {
                                        slidesPerView: 3.41875,
                                        spaceBetween: 25
                                    },
                                    // when window width is >= 1560px
                                    1560: {
                                        slidesPerView: 4.41875,
                                        spaceBetween: 30
                                    }
                                }}
                            >
                                {[...(isLoading ? dummyCoupons : rewards?.coupons || {})]?.map(
                                    (
                                        {
                                            mserp_couponcode = '123',
                                            mserp_validto = new Date(),
                                            mserp_itsdiscountvalue = 0
                                        },
                                        ii
                                    ) => (
                                        <SwiperSlide key={ii}>
                                            <CouponCard
                                                onOpen={onOpen}
                                                setSelectedCoupon={setSelectedCoupon}
                                                currency={basket?.currency || 'USD'}
                                                couponCode={mserp_couponcode}
                                                discountValue={mserp_itsdiscountvalue}
                                                expiry={mserp_validto}
                                                isLoading={isLoading}
                                            />
                                        </SwiperSlide>
                                    )
                                )}
                            </Swiper>
                        </Box>
                    ) : (
                        <Button
                            variant="red"
                            {...shopNowButtonStyle}
                            onClick={handleOnShopNowClick}
                        >
                            <FormattedMessage
                                defaultMessage="Shop Now"
                                id="my_cavenders.my_rewards.empty_card.shop_now"
                            />
                        </Button>
                    )}
                </Stack>
                <Skeleton
                    isLoaded={!loading}
                    sx={benefitsFullTileStyle}
                    dangerouslySetInnerHTML={{
                        __html: content?.['loyalty_additional_benefits_just_for_members'] || ''
                    }}
                />
                <Skeleton
                    isLoaded={!loading}
                    sx={additionalInfoFullTileStyle}
                    dangerouslySetInnerHTML={{
                        __html: content?.['loyalty_reward_terms_conditions'] || ''
                    }}
                />
            </Stack>
            <Modal
                size="lg"
                isOpen={openCouponModal}
                onClose={onClose}
                isCentered
                containerProps={{background: 'transparent'}}
            >
                <ModalOverlay />
                <ModalContent
                    containerProps={{
                        'data-testid': 'reward-coupon-modal',
                        borderRadius: '18.784px'
                    }}
                    width="content-fit"
                    sx={couponModalStyle}
                >
                    <CloseIcon
                        height="15px"
                        width="15px"
                        position="absolute"
                        right={{base: '11px', lg: '20px'}}
                        top={{base: '11px', lg: '20px'}}
                        cursor="pointer"
                        onClick={onClose}
                    />
                    <ModalBody p={0} bgColor="transparent">
                        <CouponCard {...selectedCoupon} isModalView isLoading={isLoading} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}

AccountRewards.getTemplateName = () => 'account-rewards'

export default AccountRewards
